/**
 * BillItemPicker — bill composer line typeahead.
 *
 * Mirror of `features/invoices/item-picker.tsx`, with one difference: this
 * picker queries `items.list({ usage: 'purchase' })` so the dropdown surfaces
 * **purchase**-tagged + `'both'`-tagged rows only. Items tagged `'sale'`
 * (services you only sell, not buy — e.g. machining labour) stay out of the
 * bill flow.
 *
 * Selection hydrates five fields of the bill line in one click:
 *   description · hsn · unit · rate (paise → rupees string) · taxRate (bps → percent string)
 *
 * Picking is **never required** — free-form bill lines stay legal. The
 * picker is a shortcut, not a gate, because AI-extracted bills often carry
 * descriptions the vendor used that don't match any internal SKU spelling.
 *
 * @see apps/web/src/features/invoices/item-picker.tsx — sales-side sibling
 * @see packages/api-contract/src/procedures/items.ts itemUsage
 */
import { useEffect, useRef, useState } from "react";
import { Package, Search, X } from "lucide-react";
import { Card, CardBody, DialogRoot, DialogContent, DialogTitle } from "@flint/ui";
import { trpc } from "../../../lib/trpc.ts";

/**
 * Hydration payload — string-typed to match `BillLineItem`'s form schema
 * (which stores everything as decimal strings, not bigint/millis/bps). The
 * picker does the bigint-paise → rupee-string and bps → percent-string
 * conversions so the caller just spreads.
 */
export interface PickedBillItemValues {
  description: string;
  hsn: string;
  unit: string;
  /** Rate in rupees, e.g. `'15.00'` or `'1420'`. Empty string = no default. */
  rate: string;
  /** GST percent, e.g. `'18'` or `'12'`. */
  taxRate: string;
}

export interface BillItemPickerProps {
  /** Called once the operator picks an item — never with partial values. */
  onPick: (values: PickedBillItemValues) => void;
  /** Disabled when the composer is mid-submit. */
  disabled?: boolean;
}

export function BillItemPicker({ onPick, disabled }: BillItemPickerProps) {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [debounced, setDebounced] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  // 200ms debounce — same as the invoice picker.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(input.trim()), 200);
    return () => clearTimeout(t);
  }, [input]);

  const list = trpc.items.list.useQuery(
    {
      limit: 20,
      tab: "all",
      usage: "purchase",
      ...(debounced ? { q: debounced } : {}),
    },
    { enabled: open },
  );

  useEffect(() => {
    if (!open) return undefined;
    const id = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(id);
  }, [open]);

  const items = list.data?.items ?? [];

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={disabled}
        aria-label="Pick item from catalogue"
        title="Pick from item catalogue"
        className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded text-muted hover:bg-raised hover:text-ink disabled:cursor-not-allowed disabled:opacity-30"
      >
        <Package size={14} aria-hidden />
      </button>

      <DialogRoot open={open} onOpenChange={setOpen}>
        <DialogContent className="flex max-h-[70vh] flex-col overflow-hidden p-0 sm:max-w-[480px]">
          <DialogTitle className="sr-only">Pick item from purchase catalogue</DialogTitle>

          {/* Search input */}
          <label className="flex items-center gap-2 border-b border-line px-3 py-3 text-sm">
            <Search size={14} className="shrink-0 text-muted" aria-hidden />
            <input
              ref={inputRef}
              type="search"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Name or SKU…"
              className="flex-1 bg-transparent outline-none placeholder:text-muted"
            />
            {input ? (
              <button
                type="button"
                onClick={() => setInput("")}
                aria-label="Clear search"
                className="text-muted hover:text-ink"
              >
                <X size={12} aria-hidden />
              </button>
            ) : null}
          </label>

          {/* Results */}
          <div className="flex-1 overflow-y-auto">
            {list.isLoading ? (
              <p className="p-4 text-sm text-muted">Loading…</p>
            ) : list.error ? (
              <p className="p-4 text-sm text-bad">{list.error.message}</p>
            ) : items.length === 0 ? (
              <Card>
                <CardBody className="px-4 py-6 text-center">
                  <p className="text-sm font-medium">
                    {debounced ? "No items match." : "No purchase items yet."}
                  </p>
                  <p className="mt-1 text-xs text-muted">
                    {debounced
                      ? "Try a name prefix or paste the SKU."
                      : 'Tag items in /items with usage "purchase" or "both" to use this picker.'}
                  </p>
                </CardBody>
              </Card>
            ) : (
              <ul role="listbox" aria-label="Purchase item catalogue">
                {items.map((item) => {
                  const ratePaise = item.defaultRatePaise ? BigInt(item.defaultRatePaise) : null;
                  const taxBps = item.defaultTaxRateBps ?? 1800;
                  const ratePreview =
                    ratePaise === null
                      ? "no default rate"
                      : `₹${paisePreview(ratePaise)}/${item.unit}`;
                  const taxPreview = `${formatPercentFromBps(taxBps)}% GST`;
                  return (
                    <li key={item.id}>
                      <button
                        type="button"
                        onClick={() => {
                          onPick({
                            description: item.description ?? item.name,
                            hsn: item.hsn ?? "",
                            unit: item.unit,
                            rate: ratePaise === null ? "" : paiseToRupeeString(ratePaise),
                            taxRate: String(Math.round(taxBps / 100)),
                          });
                          setOpen(false);
                          setInput("");
                        }}
                        className="w-full border-b border-line/50 px-3 py-2.5 text-left last:border-b-0 hover:bg-raised focus:bg-raised focus:outline-none"
                      >
                        <div className="flex items-baseline justify-between gap-2">
                          <span className="truncate text-sm font-medium text-ink-2">
                            {item.name}
                          </span>
                          {item.sku ? (
                            <span className="shrink-0 font-mono text-xs text-muted">
                              {item.sku}
                            </span>
                          ) : null}
                        </div>
                        <div className="mt-0.5 text-xs text-muted">
                          {ratePreview} · {taxPreview}
                          {item.hsn ? <> · HSN {item.hsn}</> : null}
                          {item.usage === "both" ? (
                            <span className="ml-1.5 inline-flex items-center rounded-full bg-raised px-1.5 text-xs font-medium uppercase tracking-wide text-ink-2">
                              both
                            </span>
                          ) : null}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </DialogContent>
      </DialogRoot>
    </>
  );
}

// ── paise/bps formatting helpers ────────────────────────────────────

/** `₹1,420` from `142000n`. Indian-locale grouping. */
function paisePreview(paise: bigint): string {
  const rupees = paise / 100n;
  return rupees.toLocaleString("en-IN");
}

/** `'15'` or `'15.50'` — the form-schema's decimal-string shape. */
function paiseToRupeeString(paise: bigint): string {
  const rupees = paise / 100n;
  const remainder = paise % 100n;
  if (remainder === 0n) return rupees.toString();
  return `${rupees}.${String(remainder).padStart(2, "0")}`;
}

/** `'18'` from `1800`, `'12.5'` from `1250`. */
function formatPercentFromBps(bps: number): string {
  const whole = Math.floor(bps / 100);
  const frac = bps % 100;
  if (frac === 0) return String(whole);
  return `${whole}.${String(frac).padStart(2, "0").replace(/0+$/, "")}`;
}
